import { Component, type ErrorInfo, type ReactNode } from 'react';
import { href } from '../app/useHashRoute.js';
import { Button } from './Button.js';
import { StatusBanner } from './StatusBanner.js';

interface State {
  error: Error | null;
}

/** A lazily loaded screen whose file is gone, usually because a new version was deployed. */
function isStaleChunk(error: Error): boolean {
  return /dynamically imported module|Importing a module script failed|Loading chunk/i.test(
    error.message,
  );
}

/**
 * Catches a crash in one screen so the rest of the app keeps working. Nothing about the
 * error is shown to the player beyond a plain message; details go to the console only.
 * Remounted per route (see App), so navigating away clears it.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Screen crashed', error, info.componentStack);
  }

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const stale = isStaleChunk(error);
    return (
      <div className="page">
        <StatusBanner
          kind="error"
          title={stale ? 'A new version is available' : 'Something went wrong on this screen'}
          action={
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                window.location.reload();
              }}
            >
              Reload
            </Button>
          }
        >
          {stale
            ? 'Reload the page to get it. If you were in a match, your seat is kept.'
            : 'Reloading usually fixes it. If you were in a match, your seat is kept.'}{' '}
          <a href={href('menu')}>Back to the Situation Room</a>
        </StatusBanner>
      </div>
    );
  }
}
